import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  UserPlus, Trash2, Save, KeyRound, ShieldOff, ShieldCheck, Copy, Check, X,
} from "lucide-react";
import CoAdminsPanel from "@/components/admin/CoAdminsPanel";

const MODULE_LABELS = {
  bookings:    "Slot bookings",
  academy:     "Academy · parents · fees",
  tournaments: "Tournaments",
  gym:         "Gym & swim",
  content:     "CMS · content · inbox",
  pricing:     "Pricing & fee sheets",
  logs:        "Webhook & sports logs",
  accounting:  "Accounting · payments · salaries",
};

// Detailed descriptions shown in the create/edit forms so admin knows exactly
// what checking each box unlocks (or removes).
const MODULE_DETAILS = {
  bookings: {
    title: "Slot Bookings",
    icon: "🎫",
    hint: "Can take & cancel slot bookings, use QR desk check-in/check-out, collect cash balance, apply dynamic pricing.",
    unlocks: "Slot matrix · Desk check-in · Cash collect · Booking refunds",
  },
  academy: {
    title: "Academy · Parents · Fees",
    icon: "🎓",
    hint: "Manage parents & wards, attendance, batches, hostel/transport, enrollment invoices, fee reminders.",
    unlocks: "Parents & Wards · Attendance · Batches · Fees · Hostel",
  },
  tournaments: {
    title: "Tournaments",
    icon: "🏆",
    hint: "Create tournaments, set entry fees, manage teams/brackets, publish results.",
    unlocks: "Tournament setup · Brackets · Entries · Results",
  },
  gym: {
    title: "Gym & Swim",
    icon: "💪",
    hint: "Gym / swim QR scanner, day-pass, membership visits, combo passes.",
    unlocks: "Gym scanner · Day-pass · Swim day · Combo",
  },
  content: {
    title: "Website Content",
    icon: "📝",
    hint: "Edit homepage, blog, gallery, news, testimonials, team. Reply to contact-form messages.",
    unlocks: "Blogs · Gallery · News · Testimonials · Team · Contact inbox",
  },
  pricing: {
    title: "Pricing & Fee Sheets",
    icon: "💰",
    hint: "Change slot prices, weekend rates, ball-machine matrix, membership plans, kit prices, GST toggles.",
    unlocks: "Service pricing · Weekend rules · Ball matrix · Kit prices · Fee sheets",
  },
  logs: {
    title: "Logs & Webhooks",
    icon: "📊",
    hint: "View mocked webhook logs, sports ingest, WhatsApp outbox / delivery audit.",
    unlocks: "Webhook logs · Sports ingest · WhatsApp outbox",
  },
  accounting: {
    title: "Accounting & Money",
    icon: "🧾",
    hint: "Ledgers (cash, bank, sales, purchases, debtors, creditors), Trial balance, Balance sheet, P&L, GSTR-1, Tally export, cash reconcile, security deposits, salary payouts.",
    unlocks: "All 11 ledger tabs · Tally export · Cash reconcile · Refunds · Purchases · Salaries",
  },
};

// One-click permission bundles for common operational roles.
// Each preset maps to a subset of PERMISSION_MODULES (backend authority).
const ROLE_PRESETS = [
  {
    key: "desk_manager",
    label: "Desk Manager",
    hint: "Front-desk: slot bookings + gym & swim check-ins",
    perms: ["bookings", "gym"],
  },
  {
    key: "cashier",
    label: "Cashier",
    hint: "Payments, pricing, invoices & fee sheets",
    perms: ["accounting", "pricing"],
  },
  {
    key: "content_editor",
    label: "Content Editor",
    hint: "Blogs, gallery, news, homepage, testimonials, inbox",
    perms: ["content"],
  },
  {
    key: "academy_coordinator",
    label: "Academy Coordinator",
    hint: "Wards, parents, batches, attendance, tournaments",
    perms: ["academy", "tournaments"],
  },
];

function TempPasswordCard({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(
      `HTP Sports Hub login\nEmail: ${data.email}\nTemporary password: ${data.temp_password}\nPlease log in and change it on first sign-in.`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mb-6 border-2 border-brand-orange bg-brand-orange/10 p-5" data-testid="temp-password-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">// Share once · temporary password</div>
          <div className="font-bold text-lg">{data.name}</div>
          <div className="text-sm text-white/70">{data.email}</div>
          <div className="mt-3 font-mono text-2xl brand-blue tracking-wider select-all">{data.temp_password}</div>
          <div className="text-xs text-white/50 mt-2">
            This password is displayed <b>once</b>. Share it with the user securely; they will be forced to change it on first login.
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={copy}
            data-testid="temp-copy"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white"
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
          </button>
          <button
            onClick={onClose}
            data-testid="temp-close"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-2 hover:bg-white/10"
          >
            <X size={14} /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminStaff() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", temp_password: "", permissions: [] });
  const [tempResult, setTempResult] = useState(null);
  const [resetFor, setResetFor] = useState(null); // {id, name, email}
  const [newResetPw, setNewResetPw] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const [m, s] = await Promise.all([
        api.get("/admin/permission-modules"),
        api.get("/admin/staff"),
      ]);
      setModules(m.data.modules || []);
      setStaff(s.data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Guard: only main admin (backend already blocks, but hide UI too)
  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;

  const togglePerm = (mod) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(mod)
        ? f.permissions.filter((p) => p !== mod)
        : [...f.permissions, mod],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/staff", form);
      toast.success("Staff account created");
      setTempResult(data);
      setForm({ name: "", email: "", phone: "", temp_password: "", permissions: [] });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveEdits = async (row) => {
    try {
      await api.patch(`/admin/staff/${row.id}`, {
        name: row.name, phone: row.phone, permissions: row.permissions, active: row.active,
      });
      toast.success("Staff updated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/staff/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Account deactivated" : "Account reactivated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resetPassword = async () => {
    if (!resetFor || newResetPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/staff/${resetFor.id}`, { reset_password: newResetPw });
      toast.success("Password reset. User must change on next login.");
      setTempResult({
        name: resetFor.name, email: resetFor.email, temp_password: newResetPw,
      });
      setResetFor(null);
      setNewResetPw("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteStaff = async (row) => {
    if (!window.confirm(`Delete staff account for ${row.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/staff/${row.id}`);
      toast.success("Staff account deleted");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-staff">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// RBAC · Staff & Sub-admins</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2">Staff & Roles.</h1>
      <p className="text-sm text-white/60 mb-8">
        Create management logins for your operations team. Assign granular module permissions —
        staff will only see the sections you have enabled for them. They cannot create other staff.
      </p>

      {tempResult && <TempPasswordCard data={tempResult} onClose={() => setTempResult(null)} />}

      {/* Co-Admins panel (owners with equal parity) */}
      <CoAdminsPanel currentUser={user} />

      <div className="border-t border-white/10 pt-8 mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-blue mb-1">// Operations · scoped staff</div>
        <h2 className="font-display text-2xl font-black uppercase tracking-tighter">Staff Accounts</h2>
        <p className="text-xs text-white/50 mt-1 max-w-xl">
          Grant limited, per-module access to your operations team. Staff cannot create other logins.
        </p>
      </div>

      {/* Create form */}
      <div className="border border-white/12 bg-[#131A2B] p-6 mb-10">
        <div className="flex items-center gap-2 mb-5">
          <UserPlus size={18} className="brand-blue" />
          <div className="font-bold uppercase tracking-wider text-sm">New staff login</div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Full name" required data-testid="staff-name"
            className="bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email" required data-testid="staff-email"
            className="bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone" data-testid="staff-phone"
            className="bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
            placeholder="Temporary password (min 6)" required minLength={6} data-testid="staff-temppw"
            className="bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <div className="md:col-span-2">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Role presets · one-click</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="staff-role-presets">
              {ROLE_PRESETS.map((p) => {
                const active =
                  p.perms.length === form.permissions.length &&
                  p.perms.every((m) => form.permissions.includes(m));
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setForm({ ...form, permissions: [...p.perms] })}
                    data-testid={`staff-preset-${p.key}`}
                    title={p.hint}
                    className={`text-left border px-3 py-2 hover:border-brand-orange transition ${
                      active ? "border-brand-orange bg-brand-orange/10" : "border-white/15"
                    }`}
                  >
                    <div className={`font-bold text-sm ${active ? "brand-orange" : "text-white"}`}>{p.label}</div>
                    <div className="text-[10px] text-white/50 mt-0.5 leading-snug">{p.hint}</div>
                  </button>
                );
              })}
            </div>

            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">
              Module permissions <span className="text-white/40 normal-case">— tick what this staff should be able to do</span>
            </div>
            <div className="space-y-2" data-testid="perm-list">
              {modules.map((m) => {
                const info = MODULE_DETAILS[m] || { title: m, hint: "", unlocks: "" };
                const on = form.permissions.includes(m);
                return (
                  <label
                    key={m}
                    data-testid={`staff-perm-${m}`}
                    className={`flex items-start gap-3 border px-4 py-3 cursor-pointer transition ${
                      on ? "border-brand-blue bg-brand-blue/10" : "border-white/12 hover:border-white/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => togglePerm(m)}
                      className="mt-1 accent-brand-blue h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xl leading-none">{info.icon}</span>
                        <span className={`font-bold text-sm ${on ? "brand-blue" : "text-white/90"}`}>
                          {info.title}
                        </span>
                        <span className={`ml-auto text-[10px] font-mono uppercase tracking-widest ${on ? "text-green-400" : "text-white/30"}`}>
                          {on ? "Allowed" : "Blocked"}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/60 mt-1 leading-snug">{info.hint}</div>
                      <div className="text-[10px] font-mono text-white/40 mt-1 truncate">
                        Unlocks: {info.unlocks}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, permissions: form.permissions.length === modules.length ? [] : [...modules] })}
              className="mt-3 text-xs font-mono uppercase tracking-widest brand-orange hover:underline"
              data-testid="staff-toggle-all"
            >
              {form.permissions.length === modules.length ? "Clear all" : "Grant all modules"}
            </button>
          </div>
          <button
            type="submit"
            data-testid="staff-create"
            className="md:col-span-2 bg-brand-blue text-white py-3 font-bold text-sm uppercase tracking-widest hover:glow-blue"
          >
            Create Staff Account
          </button>
        </form>
      </div>

      {/* Password reset modal */}
      {resetFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" data-testid="reset-modal">
          <div className="bg-[#131A2B] border border-white/12 p-6 max-w-md w-full">
            <div className="font-bold uppercase mb-1">Reset password</div>
            <div className="text-sm text-white/60 mb-4">for {resetFor.name} ({resetFor.email})</div>
            <input
              autoFocus type="text" value={newResetPw} onChange={(e) => setNewResetPw(e.target.value)}
              placeholder="New temporary password (min 6)"
              data-testid="reset-newpw"
              className="w-full bg-transparent border border-white/20 py-3 px-4 mb-4 focus:outline-none focus:border-brand-blue"
            />
            <div className="flex gap-2">
              <button
                onClick={resetPassword}
                data-testid="reset-submit"
                className="flex-1 bg-brand-blue text-white py-3 font-bold text-sm uppercase tracking-widest"
              >Reset</button>
              <button
                onClick={() => { setResetFor(null); setNewResetPw(""); }}
                className="px-4 border border-white/20 text-sm"
              >Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Staff list */}
      <div className="space-y-3">
        <div className="font-mono text-xs uppercase tracking-widest text-white/50">// Active staff accounts</div>
        {loading && <div className="text-white/40 py-6">Loading…</div>}
        {!loading && staff.length === 0 && <div className="text-white/40 py-6">No staff accounts yet.</div>}
        {staff.map((s) => (
          <StaffRow
            key={s.id}
            row={s}
            modules={modules}
            onSave={saveEdits}
            onToggleActive={toggleActive}
            onDelete={deleteStaff}
            onResetPw={() => setResetFor({ id: s.id, name: s.name, email: s.email })}
          />
        ))}
      </div>
    </div>
  );
}

function StaffRow({ row, modules, onSave, onToggleActive, onDelete, onResetPw }) {
  const [editing, setEditing] = useState({
    ...row,
    permissions: row.permissions || [],
  });
  const dirty = JSON.stringify(editing.permissions.slice().sort()) !== JSON.stringify((row.permissions || []).slice().sort())
    || editing.name !== row.name
    || (editing.phone || "") !== (row.phone || "");

  const togglePerm = (m) => {
    setEditing({
      ...editing,
      permissions: editing.permissions.includes(m)
        ? editing.permissions.filter((p) => p !== m)
        : [...editing.permissions, m],
    });
  };

  return (
    <div className="border border-white/12 bg-[#131A2B] p-5" data-testid={`staff-row-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[240px]">
          <input
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            className="bg-transparent font-bold text-lg w-full focus:outline-none border-b border-transparent focus:border-brand-blue"
            data-testid={`staff-edit-name-${row.id}`}
          />
          <div className="text-xs font-mono text-white/50 mt-1">{row.email}</div>
          <input
            value={editing.phone || ""}
            onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            placeholder="Phone"
            className="bg-transparent text-xs text-white/70 mt-1 focus:outline-none border-b border-transparent focus:border-brand-blue block"
            data-testid={`staff-edit-phone-${row.id}`}
          />
        </div>
        <div className="flex items-center gap-2">
          {!row.active && <span className="text-[10px] font-mono uppercase tracking-widest text-red-400">Deactivated</span>}
          {row.must_change_password && <span className="text-[10px] font-mono uppercase tracking-widest brand-orange">Pending PW change</span>}
          {dirty && (
            <button
              onClick={() => onSave(editing)}
              data-testid={`staff-save-${row.id}`}
              className="text-xs font-mono uppercase tracking-widest border border-brand-blue brand-blue px-3 py-1 hover:bg-brand-blue hover:text-white flex items-center gap-1"
            >
              <Save size={12} /> Save
            </button>
          )}
          <button
            onClick={onResetPw}
            data-testid={`staff-resetpw-${row.id}`}
            className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-1 hover:bg-white/10 flex items-center gap-1"
          >
            <KeyRound size={12} /> Reset PW
          </button>
          <button
            onClick={() => onToggleActive(row)}
            data-testid={`staff-toggle-${row.id}`}
            className="text-xs font-mono uppercase tracking-widest border border-white/20 px-3 py-1 hover:bg-white/10 flex items-center gap-1"
          >
            {row.active ? <><ShieldOff size={12} /> Deactivate</> : <><ShieldCheck size={12} /> Reactivate</>}
          </button>
          <button
            onClick={() => onDelete(row)}
            data-testid={`staff-delete-${row.id}`}
            className="text-xs font-mono uppercase tracking-widest border border-red-500/40 text-red-400 px-3 py-1 hover:bg-red-500/10 flex items-center gap-1"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-1.5" data-testid={`edit-perm-list-${row.id}`}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
          Edit permissions — tick / untick anytime
        </div>
        {modules.map((m) => {
          const info = MODULE_DETAILS[m] || { title: m, hint: "", icon: "•" };
          const on = editing.permissions.includes(m);
          return (
            <label
              key={m}
              className={`flex items-start gap-3 border px-3 py-2 cursor-pointer text-xs transition ${
                on ? "border-brand-blue bg-brand-blue/8" : "border-white/10 hover:border-white/25"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => togglePerm(m)}
                className="mt-0.5 accent-brand-blue"
                data-testid={`staff-editperm-${row.id}-${m}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="leading-none">{info.icon}</span>
                  <span className={`font-bold ${on ? "brand-blue" : "text-white/80"}`}>{info.title}</span>
                  <span className={`ml-auto text-[9px] font-mono uppercase tracking-widest ${on ? "text-green-400" : "text-white/30"}`}>
                    {on ? "Allowed" : "Blocked"}
                  </span>
                </div>
                <div className="text-[10px] text-white/50 mt-0.5 leading-snug truncate">{info.hint}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
